import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Badge,
  CircularProgress,
  Fade,
  Popover,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  SignalWifi4Bar as ConnectedIcon,
  SignalWifiOff as DisconnectedIcon,
  SignalWifi0Bar as ConnectingIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Schedule as ScheduleIcon,
  ErrorOutline as ErrorIcon,
  CheckCircleOutline as SuccessIcon,
} from '@mui/icons-material';
import { useWebSocketConnection } from '../../contexts/WebSocketContext';
import { monitoringWebSocket } from '../../services/websocketService'; // WebSocket 인스턴스 직접 참조

interface ConnectionStatusIndicatorProps {
  showLabel?: boolean;
  size?: 'small' | 'medium' | 'large';
  showReconnectButton?: boolean;
  poolId?: string | number;
  showDetailedStatus?: boolean;
}

/**
 * WebSocket 연결 상태를 표시하는 컴포넌트
 * - 연결 상태 표시 (connected, connecting, disconnected)
 * - 연결 시간, 재연결 시도 횟수 등 상세 정보 표시 (선택적)
 * - 수동 재연결 버튼 제공
 */
const ConnectionStatusIndicator: React.FC<ConnectionStatusIndicatorProps> = ({
  showLabel = false,
  size = 'medium',
  showReconnectButton = true,
  poolId,
  showDetailedStatus = false,
}) => {
  const { connectionStatus, isConnected, isConnecting, isDisconnected, reconnect } = useWebSocketConnection();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [lastConnectedTime, setLastConnectedTime] = useState<Date | null>(null);
  const [connectionInfo, setConnectionInfo] = useState({
    attempts: 0,
    lastAttempt: null as Date | null,
    uptime: '0s',
  });
  
  // 아이콘 크기 설정
  const iconSizes = {
    small: 16,
    medium: 20,
    large: 24,
  };
  
  // 연결 상태가 변경될 때 시간 정보 업데이트
  useEffect(() => {
    if (isConnected) {
      setLastConnectedTime(new Date());
    }
    
    // WebSocket 서비스로부터 추가 정보 가져오기
    const updateConnectionInfo = () => {
      // WebSocketService 인스턴스의 메서드 호출
      setConnectionInfo({
        attempts: monitoringWebSocket.getReconnectAttempts() || 0,
        lastAttempt: monitoringWebSocket.getLastAttemptTime(),
        uptime: isConnected 
          ? formatUptime(monitoringWebSocket.getConnectedSince() || lastConnectedTime) 
          : '0s',
      });
    };
    
    updateConnectionInfo();
    const infoInterval = setInterval(updateConnectionInfo, 1000);
    
    return () => clearInterval(infoInterval);
  }, [connectionStatus, isConnected, lastConnectedTime]);
  
  // 상태별 색상 및 아이콘 설정
  const statusConfig = {
    connected: {
      color: 'success.main',
      icon: <ConnectedIcon sx={{ fontSize: iconSizes[size] }} />,
      tooltip: '실시간 모니터링 WebSocket 연결됨 (백엔드 API)',
      label: '연결됨',
    },
    connecting: {
      color: 'warning.main',
      icon: <ConnectingIcon sx={{ fontSize: iconSizes[size] }} />,
      tooltip: '실시간 모니터링 WebSocket 연결 중…',
      label: '연결 중...',
      spinner: true,
    },
    disconnected: {
      color: 'error.main',
      icon: <DisconnectedIcon sx={{ fontSize: iconSizes[size] }} />,
      tooltip: '실시간 모니터링 WebSocket 끊김 (백엔드 API)',
      label: '연결 끊김',
    },
  };
  
  const config = statusConfig[connectionStatus];
  
  // 업타임 포맷팅 함수
  const formatUptime = (startTime: Date | null): string => {
    if (!startTime) return '0s';
    
    const diff = Math.floor((new Date().getTime() - startTime.getTime()) / 1000);
    
    if (diff < 60) return `${diff}초`;
    if (diff < 3600) return `${Math.floor(diff / 60)}분 ${diff % 60}초`;
    return `${Math.floor(diff / 3600)}시간 ${Math.floor((diff % 3600) / 60)}분`;
  };
  
  // 재연결 핸들러
  const handleReconnect = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsReconnecting(true);
    
    // 재연결 효과를 위해 약간의 지연 추가
    setTimeout(() => {
      reconnect(poolId);
      setIsReconnecting(false);
    }, 500);
  };
  
  // 상세 정보 표시/숨김 핸들러
  const handleDetailClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleDetailClose = () => {
    setAnchorEl(null);
  };
  
  const open = Boolean(anchorEl);
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Tooltip title={config.tooltip}>
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            (isReconnecting || (connectionStatus === 'connecting')) ? (
              <Fade in={true}>
                <CircularProgress
                  size={iconSizes[size] / 2}
                  thickness={6}
                  sx={{ color: isReconnecting ? 'primary.main' : config.color }}
                />
              </Fade>
            ) : null
          }
        >
          <Box sx={{ color: config.color }}>{config.icon}</Box>
        </Badge>
      </Tooltip>
      
      {showLabel && (
        <Typography
          variant={size === 'small' ? 'caption' : 'body2'}
          color={config.color}
          sx={{ fontWeight: 'medium' }}
        >
          {isReconnecting ? '재연결 중...' : config.label}
        </Typography>
      )}
      
      {showReconnectButton && !isConnected && !isReconnecting && (
        <Tooltip title="재연결 시도">
          <IconButton
            size="small"
            onClick={handleReconnect}
            sx={{ ml: 0.5 }}
            color="primary"
          >
            <RefreshIcon sx={{ fontSize: iconSizes[size] }} />
          </IconButton>
        </Tooltip>
      )}
      
      {showDetailedStatus && (
        <>
          <Tooltip title="연결 상태 상세 정보">
            <IconButton size="small" onClick={handleDetailClick} sx={{ ml: 0.5 }}>
              <InfoIcon sx={{ fontSize: iconSizes[size] }} />
            </IconButton>
          </Tooltip>
          
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleDetailClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            <Paper sx={{ width: 300, p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                WebSocket 연결 상태
              </Typography>
              <Divider sx={{ mb: 1 }} />
              
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    {isConnected ? <SuccessIcon color="success" /> : 
                     isConnecting ? <ScheduleIcon color="warning" /> : 
                     <ErrorIcon color="error" />}
                  </ListItemIcon>
                  <ListItemText 
                    primary="현재 상태" 
                    secondary={
                      isConnected ? '연결됨' : 
                      isConnecting ? '연결 중...' : 
                      '연결 끊김'
                    } 
                  />
                </ListItem>
                
                {isConnected && (
                  <ListItem>
                    <ListItemIcon>
                      <ScheduleIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary="연결 유지 시간" 
                      secondary={connectionInfo.uptime} 
                    />
                  </ListItem>
                )}
                
                {connectionInfo.attempts > 0 && (
                  <ListItem>
                    <ListItemIcon>
                      <RefreshIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary="재연결 시도" 
                      secondary={`${connectionInfo.attempts}회`} 
                    />
                  </ListItem>
                )}
              </List>
              
              {!isConnected && (
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton 
                    color="primary" 
                    onClick={(e) => {
                      handleReconnect(e);
                      handleDetailClose();
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Box>
              )}
            </Paper>
          </Popover>
        </>
      )}
    </Box>
  );
};

export default ConnectionStatusIndicator; 